import SearchableSelect from '../search/SearchableSelect';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { getSplitTemplates } from '../../services/api';

const PAY_INTERVALS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
];

function TemplateIncomeLinker({
  incomes,
  templateIncomeMappings,
  setTemplateIncomeMappings,
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getSplitTemplates();
      // Filter to only income-type templates
      const incomeTemplates = response.data.filter(t => t.transaction_type === 'income');
      setTemplates(incomeTemplates);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load split templates');
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (templateId, incomeId) => {
    setTemplateIncomeMappings(prev => ({
      ...prev,
      [templateId]: incomeId || null
    }));
  };

  const getIncomeLabel = (income) => {
    return income.label || `Income ${income.id}`;
  };

  const getFrequencyLabel = (frequency) => {
    const interval = PAY_INTERVALS.find(p => p.value === frequency);
    return interval ? interval.label : frequency;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Link Templates to Income Sources
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Link each split template to an income source. The template will use that income's pay frequency for calculations.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Income Sources Summary */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>
          Your Income Sources:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {incomes.filter(i => i.gross_annual > 0).map(income => (
            <Chip
              key={income.id}
              label={`${getIncomeLabel(income)} (${getFrequencyLabel(income.frequency || 'bi-weekly')})`}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      </Paper>

      {templates.length === 0 ? (
        <Alert severity="info">
          No income split templates found. You can skip this step or create templates in the Split Templates page.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {templates.map(template => (
            <Grid item xs={12} md={6} key={template.id}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {template.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {template.allocations?.length || 0} allocations
                      {template.default_amount && ` | Default: $${template.default_amount}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {template.is_scenario && (
                      <Chip label="🧪 Scenario" color="warning" size="small" />
                    )}
                    <Chip
                      label="Income"
                      color="success"
                      size="small"
                    />
                  </Box>
                </Box>

                <FormControl fullWidth size="small">
                  <InputLabel>Link to Income Source</InputLabel>
                  <SearchableSelect
                    value={templateIncomeMappings[template.id] || ''}
                    onChange={(e) => handleMappingChange(template.id, e.target.value)}
                    label="Link to Income Source"
                  >
                    <MenuItem value="">
                      <em>Not linked</em>
                    </MenuItem>
                    {incomes.filter(i => i.gross_annual > 0).map(income => (
                      <MenuItem key={income.id} value={income.id}>
                        {getIncomeLabel(income)} ({getFrequencyLabel(income.frequency || 'bi-weekly')})
                      </MenuItem>
                    ))}
                  </SearchableSelect>
                </FormControl>

                {templateIncomeMappings[template.id] && (
                  <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                    Will use {getFrequencyLabel(
                      incomes.find(i => i.id === templateIncomeMappings[template.id])?.frequency || 'bi-weekly'
                    )} frequency
                  </Typography>
                )}

                {/* Show allocations preview */}
                {template.allocations && template.allocations.length > 0 && (
                  <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" color="text.secondary">
                      Allocations:
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {template.allocations.slice(0, 3).map(alloc => (
                        <Typography key={alloc.id} variant="caption" display="block" sx={{ ml: 1 }}>
                          {alloc.category_name}: {alloc.value}{alloc.allocation_type === 'percentage' ? '%' : ''}
                        </Typography>
                      ))}
                      {template.allocations.length > 3 && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          +{template.allocations.length - 3} more...
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Summary of linked templates */}
      {Object.keys(templateIncomeMappings).filter(k => templateIncomeMappings[k]).length > 0 && (
        <Paper sx={{ p: 2, mt: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
          <Typography variant="subtitle2" gutterBottom>
            Linked Templates Summary
          </Typography>
          {Object.entries(templateIncomeMappings)
            .filter(([_, incomeId]) => incomeId)
            .map(([templateId, incomeId]) => {
              const template = templates.find(t => t.id === parseInt(templateId));
              const income = incomes.find(i => i.id === incomeId);
              if (!template || !income) return null;
              return (
                <Typography key={templateId} variant="body2">
                  {template.name} → {getIncomeLabel(income)} ({getFrequencyLabel(income.frequency || 'bi-weekly')})
                </Typography>
              );
            })}
        </Paper>
      )}
    </Box>
  );
}

export default TemplateIncomeLinker;
